import { yupResolver } from '@hookform/resolvers/yup';
import React, { memo } from 'react';
import { useForm } from 'react-hook-form';
import Select from 'react-select';

import styles from './OldDeviceModel.module.css';
import Step from '../Step/Step';
import { StepType } from '../../../state/types';
import { State, useState } from '../../../state/store';
import FormContainer from '../../FormContainer/FormContainer';
import { oldDeviceModelSchema } from '../../../state/validation';
import { commonSelectStyles } from '../../../state/constants';

const OldDeviceModel: React.FC = memo(() => {
  const { model, brand, brands, modelsMyBrand, dispatch } = useState('model', 'brand', 'brands', 'modelsMyBrand');
  const {
    register,
    trigger,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<State>({
    resolver: yupResolver(oldDeviceModelSchema),
  });

  const prepareRegisterProps = (name: keyof State, options = { required: true }) => {
    const registerProps = { ...register(name, options) };
    const onChange = registerProps.onChange;
    registerProps.onChange = (e) => {
      /**
       * Trigger validation right when user is typing and update state in case there are no errors
       */
      return onChange(e)
        .then(() => trigger(name))
        .then(() => {
          if (!errors[name]) {
            dispatch('update', {
              [name]: watch(name),
            });
          }
        });
    };
    return registerProps;
  };

  setValue('model', model);
  setValue('brand', brand);
  prepareRegisterProps('model');
  prepareRegisterProps('brand');

  const brandOption = brand ? brands.find((s) => s.label === brand) : null;
  const modelOption = model ? modelsMyBrand.find((p) => p.value === model) : null;
  const selectedModelOptions = brandOption ? modelsMyBrand : [];

  const isModelRequired = brand !== 'Any Other Smartphone' && !modelOption;

  return (
    <Step
      stepToShow={StepType.OldDeviceModel}
      stepBack={StepType.NewDeviceCheck}
      stepNext={StepType.OldDeviceStatus}
      isValid={isValid && !isModelRequired}
    >
      <div className={styles.OldDeviceModel} data-testid="OldDeviceModel">
        <form className="w-full bg-gray-100 rounded">
          <FormContainer header={'Select your old Device'}>
            <div className="md:items-center mb-6 mx-auto max-w-xs">
              <div className="mb-6">
                <label className="block text-gray-500 font-bold md:text-center mb-1 md:mb-0 pr-4" htmlFor="brand">
                  Choose Phone Vendor:
                </label>
              </div>
              <div>
                <Select
                  inputId="brand"
                  value={brandOption}
                  onBlur={() => trigger('brand')}
                  onChange={(option) => {
                    setValue('brand', option?.label || '', { shouldValidate: true });
                    setValue('model', '', { shouldValidate: true });

                    dispatch('update', {
                      brand: option?.label || '',
                      model: '',
                      modelsMyBrand: [],
                    });

                    if (option?.label) {
                      dispatch('phonesByVendor', option.label);
                    }
                  }}
                  options={brands}
                  styles={commonSelectStyles}
                />
                <br />
                <div className="pt-2 md:text-center text-red-400">{errors.brand?.message}</div>
              </div>
            </div>
            {brand !== 'Any Other Smartphone' && (
              <div className="md:items-center mb-6 mx-auto max-w-xs">
                <div className="mb-6">
                  <label className="block text-gray-500 font-bold md:text-center mb-1 md:mb-0 pr-4" htmlFor="model">
                    {brand !== 'Any Other Smartphone' ? 'Choose' : 'Your'} Phone Model:
                  </label>
                </div>
                <div>
                  <Select
                    inputId="model"
                    isDisabled={!brand}
                    value={modelOption || null}
                    onBlur={() => trigger('model')}
                    onChange={(option) => {
                      const maxValue = option?.priceMax || '0';
                      const minValue = option?.priceMin || '0';
                      setValue('tradeInValue', maxValue);
                      setValue('tradeInValueBad', minValue);
                      dispatch('update', {
                        tradeInValue: maxValue,
                        tradeInValueBad: minValue,
                      });
                      setValue('model', option?.value || '', { shouldValidate: true });
                      dispatch('update', {
                        model: option?.value || '',
                      });
                    }}
                    options={brand ? selectedModelOptions : []}
                    styles={commonSelectStyles}
                  />
                  <br />
                  <div className="pt-2 md:text-center text-red-400">
                    {isModelRequired && <div>Model is required</div>}
                    {errors.model?.message}
                  </div>
                </div>
                <div className="text-center mt-6">
                  If your phone does not appear on the drop down list please choose Any Other Smartphone.
                </div>
              </div>
            )}
          </FormContainer>
        </form>
      </div>
    </Step>
  );
});

export default OldDeviceModel;
